import { PageLayout } from '@/shared/components/PageLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { config } from '@/lib/constants'
import { content } from '@/lib/content'
import {
  Calendar,
  Clock,
  Heart,
  Gift,
  Utensils,
  Dices,
  Music,
  ExternalLink,
  MapPin
} from 'lucide-react'

const activityIcons = {
  'Queen of Hearts': Heart,
  'Xtreme Bar Bingo': Dices,
  'KC Raffle': Gift,
  'Fish Fry': Utensils,
}

const activityColors = {
  'Queen of Hearts': 'from-rose-500/20 to-rose-600/10',
  'Xtreme Bar Bingo': 'from-violet-500/20 to-violet-600/10',
  'KC Raffle': 'from-amber-500/20 to-amber-600/10',
  'Fish Fry': 'from-sky-500/20 to-sky-600/10',
}

const activities = content.activities.events

export default function ActivitiesPage() {
  return (
    <PageLayout
      title={content.activities.seo.title}
      description={content.activities.seo.description}
    >
      {/* Hero Header */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 pattern-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />

        <div className="relative content-container text-center">
          <Badge variant="outline" className="mb-6 border-primary/50 bg-primary/10 text-primary px-4 py-1.5">
            <Calendar className="mr-2 h-3.5 w-3.5" />
            Weekly Schedule
          </Badge>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            {content.activities.heading}
          </h1>

          <Separator className="w-16 bg-primary mx-auto mb-6" />

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {content.activities.description}
          </p>
        </div>
      </section>

      {/* Weekly Events Grid */}
      <section className="section-padding pt-0">
        <div className="content-container">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {activities.map((activity) => {
              const IconComponent = activityIcons[activity.name] || Calendar
              const gradientClass = activityColors[activity.name] || 'from-primary/20 to-primary/10'

              return (
                <Card
                  key={activity.name}
                  className="group overflow-hidden bg-card border-border hover:border-primary/50 transition-all duration-300 card-hover"
                >
                  {/* Image with Overlay */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={activity.image}
                      alt={activity.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${gradientClass} to-background/90`} />

                    {/* Time Badge */}
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-background/90 text-foreground backdrop-blur-sm border-0">
                        <Clock className="mr-1.5 h-3 w-3 text-primary" />
                        {activity.startTime}
                        {activity.endTime && ` - ${activity.endTime}`}
                      </Badge>
                    </div>

                    {/* Day Badge */}
                    <div className="absolute top-4 left-4">
                      <Badge variant="outline" className="bg-primary/90 text-primary-foreground border-0">
                        {activity.subtitle}
                      </Badge>
                    </div>

                    {/* Icon */}
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                  </div>

                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl text-foreground">
                      {activity.name}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      {activity.description}
                    </p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Quick Schedule Reference */}
      <section className="py-12 bg-secondary/30">
        <div className="content-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-foreground mb-8">
              Weekly at a Glance
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { day: 'Tuesday', event: 'Queen of Hearts', time: '7:00 PM', icon: Heart },
                { day: 'Tuesday', event: 'Bar Bingo', time: '7:30 PM', icon: Dices },
                { day: 'Wednesday', event: 'KC Raffle', time: '6:30 PM', icon: Gift },
                { day: 'Friday', event: 'Fish Fry', time: '4-7 PM', icon: Utensils },
              ].map((item) => (
                <div
                  key={`${item.day}-${item.event}`}
                  className="bg-card border border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors"
                >
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-xs text-primary font-medium uppercase tracking-wider mb-1">
                    {item.day}
                  </p>
                  <p className="text-sm font-semibold text-foreground mb-1">
                    {item.event}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {item.time}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Concert Series Section */}
      <section className="section-padding">
        <div className="content-container">
          <Card className="max-w-4xl mx-auto bg-card border-border overflow-hidden">
            <div className="md:flex">
              {/* Left side - decorative */}
              <div className="md:w-1/3 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center warm-glow">
                    <Music className="h-10 w-10 text-primary" />
                  </div>
                  <p className="text-primary font-semibold uppercase tracking-wider text-sm">
                    Live Music
                  </p>
                </div>
              </div>

              {/* Right side - content */}
              <div className="md:w-2/3 p-8 md:p-10">
                <Badge variant="outline" className="mb-4 border-primary/50 text-primary">
                  Special Events
                </Badge>

                <h2 className="text-3xl font-bold text-foreground mb-4">
                  {content.activities.concertSeries.heading}
                </h2>

                <p className="text-muted-foreground leading-relaxed mb-6">
                  {content.activities.concertSeries.description}
                </p>

                <a
                  href={config.facebookLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="cursor-pointer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {content.activities.concertSeries.ctaText}
                  </Button>
                </a>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Location Reminder */}
      <section className="py-12 bg-secondary/30">
        <div className="content-container">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-foreground font-semibold">
                All events held at KC Hall of O'Fallon
              </p>
              <p className="text-muted-foreground">
                Located across from O'Fallon Community Park on Highway 50
              </p>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}
